/***
 *
 *   PROFILE
 *   Phase 8 — ProfileSection chrome. Keep live Form APIs (no avatar upload —
 *   live has none).
 *
 **********/

import React, { useContext } from 'react';
import { Form, Card, SettingsWrapper, Animate, Skeleton, UserAvatar } from 'components/lib';
import { AuthContext } from 'app/auth';
import { useTranslate, LOCALE_STORAGE_KEY, LOCALES } from 'app/translations';
import { useApiContext } from 'app/api';
import { useQuery } from 'react-query';
import Style from './profile.tailwind';

export function Profile(props) {
    const { _t } = useTranslate();
    const { callApi } = useApiContext();
    const authContext = useContext(AuthContext);
    const user = useQuery('user', () => callApi({ url: '/api/user' }));
    const data = user?.data?.data;
    const fullName = data
        ? `${data.first_name || ''} ${data.last_name || ''}`.trim()
        : '';

    return (
        <SettingsWrapper>
            <Animate>
                <div className={Style.stack}>
                    <div>
                        <h1 className={Style.title}>{_t('profile.title')}</h1>
                        <p className={Style.description}>{_t('profile.updateInfo')}</p>
                    </div>

                    {user.isLoading ? (
                        <Skeleton className="h-64 w-full" />
                    ) : (
                        <Card
                            title={_t('profile.edit.label')}
                            description={_t('profile.edit.text')}>
                            {data && (
                                <div className={Style.body}>
                                    <div className={Style.identity}>
                                        <UserAvatar name={fullName || data.email} size="lg" />
                                        <div className="min-w-0">
                                            <p className={Style.name}>{fullName || data.email}</p>
                                            <p className={Style.email}>{data.email}</p>
                                        </div>
                                    </div>

                                    <Form
                                        buttonText={_t('save')}
                                        url="/api/user"
                                        method="PATCH"
                                        data={{
                                            first_name: {
                                                label: _t('firstName.title'),
                                                type: 'text',
                                                required: true,
                                                value: data.first_name,
                                                errorMessage: _t('firstName.empty'),
                                            },
                                            last_name: {
                                                label: _t('lastName.title'),
                                                type: 'text',
                                                required: true,
                                                value: data.last_name,
                                                errorMessage: _t('lastName.empty'),
                                            },
                                            email: {
                                                label: _t('email.yourEmail'),
                                                type: 'email',
                                                required: true,
                                                value: data.email,
                                                errorMessage: _t('email.empty'),
                                            },
                                            language: {
                                                label: _t('language.title'),
                                                type: 'select',
                                                required: true,
                                                value: data.language,
                                                options: [
                                                    { value: 'en', label: _t('language.english') },
                                                    { value: 'fr', label: _t('language.french') },
                                                ],
                                            },
                                            ...(data?.accounts?.length > 1 && {
                                                default_account: {
                                                    label: _t('organization.default'),
                                                    type: 'select',
                                                    default: data.default_account,
                                                    options: data.accounts.map((x) => ({
                                                        value: x.id,
                                                        label: x.name,
                                                    })),
                                                },
                                            }),
                                        }}
                                        callback={(res) => {
                                            authContext.update({
                                                ...res.data,
                                            });
                                            if (res.data.language !== data.language) {
                                                try {
                                                    if (LOCALES.includes(res.data.language)) {
                                                        localStorage.setItem(
                                                            LOCALE_STORAGE_KEY,
                                                            res.data.language,
                                                        );
                                                    }
                                                } catch (err) {
                                                    // not persisting is survivable — reload still applies profile language
                                                }
                                                window.location.reload();
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </Card>
                    )}
                </div>
            </Animate>
        </SettingsWrapper>
    );
}
